/**
 * CommandCenter — the company "Operating Status" home (`/`).
 *
 * Home Redesign (Phase 2), built to docs/mockups/home-redesign-nexus-palette.html
 * (directions A Operating-Status + C plain-English + D schedule-spine / A-R):
 *
 *   verdict statement + status pill  (one honest line — green AND the scary reds)
 *   → 4 vitals
 *   → adaptive center: schedule-spine (paid-for-work) OR A/R aging (billing)
 *   → "Do now" + Tier-2 gap-closers (when cash is at risk)
 *   → Money / Compliance lanes (from the ranked feed)
 *   → drill links + evidence footer + basis-of-accounting disclaimer
 *
 * Compliance (docs/METHODS-AND-COMPLIANCE-FOUNDATION.md): the verdict + cash carry
 * "how this is calculated" provenance; figures are labeled tax-basis/unaudited;
 * gap-closers are OPTIONS the user chooses (never auto-acted); standing "not advice".
 *
 * Reuses the ranked feed (useCommandCenterFeed) + nav counts + cash forecast.
 * (The old Dashboard at /numbers/legacy was retired 2026-06-30; its unique
 * cash-flow forecast moved to /finance/cashflow.)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  AlertCircle, CalendarCheck, FileText, Wallet, Coins, FilePenLine, CheckCircle2,
  Clock, ListChecks, BarChart3, Sparkles, ScanLine, ArrowRight, ShieldCheck, Landmark,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { GlossaryText } from '@/components/ui/Glossary';
import { ErrorBanner } from '@/components/ui/AsyncGuard';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAppStore } from '@/stores/useAppStore';
import { useRole } from '@/hooks/useRole';
import { useNavCounts } from '@/hooks/useNavCounts';
import { computeAttentionState, selectHomeAttention, type AttentionItem } from '@/components/layout/nav-attention';
import { useCompanyCash } from '@/hooks/useCompanyCash';
import { useArAging } from '@/hooks/useArAging';
import {
  useCommandCenterFeed, type CommandCenterCardType, type CommandCenterItem,
} from '@/hooks/useCommandCenterFeed';
import { formatISODate } from '@/lib/dates';
import { resolveOperatingModel } from '@/lib/preset-defaults';
import { ExpectedRevenueCard } from '@/components/home/ExpectedRevenueCard';
import { ArAgingCenter } from '@/components/home/ArAgingCenter';
import { RecommendationProvenance, BasisBadge } from '@/components/home/RecommendationProvenance';
// Usability audit 2026-06-29 (D-activation): the first-run guidance lived only on the
// legacy /numbers/legacy dashboard, so a brand-new tenant landed on an empty Command
// Center with no next step. Both components self-suppress (backend `show` flag + 24h
// dismiss + 60-day auto-hide; FormationReadinessCard hides for non-S-corps), so they're
// invisible to established tenants — pure wiring onto the live home.
import { FirstRunChecklist } from '@/components/dashboard/FirstRunChecklist';
import { GetStartedHero } from '@/components/dashboard/GetStartedHero';
import { FormationReadinessCard } from '@/components/dashboard/FormationReadinessCard';
import {
  computeCompanyVerdict, verdictPillLabel, type CompanySignals, type VerdictTier,
} from '@/lib/company-verdict';

function fmtCents(cents: number | null): string | null {
  if (cents == null) return null;
  const d = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: Math.abs(d) >= 1000 ? 0 : 2,
    maximumFractionDigits: Math.abs(d) >= 1000 ? 0 : 2,
  }).format(d);
}

const CARD_TYPE_ICON: Record<CommandCenterCardType, React.ComponentType<{ className?: string }>> = {
  compliance: CalendarCheck, collection: AlertCircle, payment: Wallet, renewal: FilePenLine,
  approval: CheckCircle2, pto: Clock, categorization: Coins, checklist: ListChecks,
  anomaly: BarChart3, onboarding: Sparkles,
};
const CARD_TYPE_TONE: Record<CommandCenterCardType, string> = {
  compliance: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  collection: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  payment: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  renewal: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  approval: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  pto: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  categorization: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  checklist: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  anomaly: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  onboarding: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
};

const MONEY_TYPES: CommandCenterCardType[] = ['collection', 'payment', 'categorization', 'anomaly'];
const COMPLIANCE_TYPES: CommandCenterCardType[] = ['compliance', 'renewal', 'checklist', 'approval', 'pto', 'onboarding'];

const VERDICT_STYLE: Record<VerdictTier, { pill: string; bar: string; border: string }> = {
  at_risk: {
    pill: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    bar: 'bg-rose-500', border: 'border-l-rose-500',
  },
  watch: {
    pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    bar: 'bg-amber-500', border: 'border-l-amber-500',
  },
  healthy: {
    pill: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    bar: 'bg-emerald-500', border: 'border-l-emerald-500',
  },
  // First-run: a guiding (not graded) tone — teal, distinct from the green "healthy".
  setup: {
    pill: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
    bar: 'bg-primary-500', border: 'border-l-primary-500',
  },
};

export default function CommandCenter() {
  const navigate = useNavigate();
  const { user, activeEntity } = useAppStore();
  const { isEmployee, hasMinRole, role } = useRole();

  // Employees use EmployeeLayout — bounce (backend would 403 anyway).
  useEffect(() => {
    if (isEmployee) navigate('/employee', { replace: true });
  }, [isEmployee, navigate]);

  const { feed, loading: feedLoading, error } = useCommandCenterFeed('today');
  const { counts } = useNavCounts();
  // Cash forecast is finance-read tier — only fetch for bookkeeper+ (else 403).
  const { cash, loading: cashLoading } = useCompanyCash(hasMinRole('bookkeeper'));
  // A/R aging — single shared fetch (verdict signal + ArAgingCenter display).
  // Finance-read tier; viewers can read aging (ensureFinanceReadAccess).
  const { data: aging, agedArCents, loading: agingLoading } = useArAging(hasMinRole('viewer'));

  const operatingModel = resolveOperatingModel(activeEntity?.onboardingPreset);
  const firstName = user?.fullName?.split(' ')[0] ?? 'there';

  // First-run window (entity < 60d AND required setup items remain) — gates the
  // GetStartedHero + the "getting set up" verdict tone. Same backend signal the
  // FirstRunChecklist uses; a 403/empty just means "not first-run".
  const [firstRunShow, setFirstRunShow] = useState(false);
  useEffect(() => {
    if (!activeEntity?.id) { setFirstRunShow(false); return; }
    let active = true;
    api.getFirstRunChecklist()
      .then((d) => { if (active) setFirstRunShow(!!d?.show); })
      .catch(() => { if (active) setFirstRunShow(false); });
    return () => { active = false; };
  }, [activeEntity?.id]);
  const isFirstRun = firstRunShow;

  const items = feed.items || [];
  // Expenses still needing docs (protects deductions) — from the ranked feed.
  const docsToFix = items.filter((i) => i.card_type === 'categorization').length;

  const verdict = useMemo(() => {
    const signals: CompanySignals = {
      cashCents: cash.cashCents,
      cashKnown: cash.cashKnown,
      lowestProjectedCents: cash.lowestProjectedCents,
      overdueBills: counts.overdueBills ?? 0,
      complianceDueSoon: counts.complianceDueSoon ?? 0,
      pendingApprovals: counts.pendingApprovals ?? 0,
      overdueArCents: agedArCents,
      docsToFix,
      operatingModel,
      firstRun: isFirstRun,
      trackedDeadlines: counts.complianceDueSoon ?? 0,
    };
    return computeCompanyVerdict(signals);
  }, [cash, counts, agedArCents, docsToFix, operatingModel, isFirstRun]);

  // Home Redesign Step 1 (2026-06-17): surface the attention categories that
  // have NO other Home presence (missing-W9, policies-review,
  // onboarding-unstarted, AI-budget) as cards. Same role+count filtering as the
  // sidebar (computeAttentionState), so nothing is stranded when Step 2 collapses
  // the sidebar attention block into a single pill. Bills/compliance/approvals/PTO
  // already appear as vitals/lanes and are intentionally NOT re-surfaced here.
  const homeAttn = useMemo(
    () => selectHomeAttention(computeAttentionState(counts, role).items),
    [counts, role],
  );

  const nowItem = items[0];
  // Render the top item as "Do now" only when there's no at-risk gap-closer set;
  // when it IS the Do-now, drop it from the lanes so it doesn't render twice.
  const nowShown = verdict.gapClosers.length === 0 && !!nowItem;
  const laneSource = nowShown ? items.filter((i) => i.id !== nowItem.id) : items;
  const moneyItems = laneSource.filter((i) => MONEY_TYPES.includes(i.card_type)).slice(0, 4);
  const complianceItems = laneSource.filter((i) => COMPLIANCE_TYPES.includes(i.card_type)).slice(0, 4);
  const vStyle = VERDICT_STYLE[verdict.tier];

  const loading = feedLoading || cashLoading || agingLoading;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      {/* No-commingling context chip */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          {activeEntity?.legalName && <span className="ml-2 font-medium text-slate-700 dark:text-slate-300">· {activeEntity.legalName}</span>}
        </p>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-2.5 py-1 text-[11px] font-semibold text-rose-700 dark:text-rose-300">
          <ShieldCheck className="w-3.5 h-3.5" /> No commingling · business only
        </span>
      </div>

      {error && <ErrorBanner message={error} retry={() => window.location.reload()} />}

      {/* ── Verdict (the one honest status line) ── */}
      {loading ? (
        <Card padding="lg"><Skeleton className="h-8 w-2/3 mb-3" /><Skeleton className="h-4 w-1/2" /></Card>
      ) : (
        <Card padding="lg" className={`border-l-4 ${vStyle.border}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
                {firstName} · operating status
              </p>
              <h1 className="font-headline text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                <GlossaryText text={verdict.statement} inlineFirstUse={false} />
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2"><GlossaryText text={verdict.subline} inlineFirstUse={false} /></p>
              <RecommendationProvenance
                lines={[
                  'Ranked from bank-true cash, the 13-week cash-flow forecast, overdue A/R + bills, and compliance items.',
                  'Cash is bank-true only after a bank statement is reconciled; otherwise we ask you to reconcile rather than guess.',
                ]}
                cite="Cash-flow forecast · cash basis, unaudited"
              />
            </div>
            <span className={`shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${vStyle.pill}`}>
              <span className="w-2 h-2 rounded-full bg-current" /> {verdictPillLabel(verdict.tier)}
            </span>
          </div>

          {/* 4 vitals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <Vital
              label="Cash · bank-true" tone="emerald"
              value={cash.cashKnown ? fmtCents(cash.cashCents) ?? '—' : 'Reconcile'}
              sub={cash.cashKnown ? 'reconciled to bank' : 'reconcile a statement'}
              // Reconcile is admin-gated (App.tsx) — only link admins there.
              to={!cash.cashKnown && hasMinRole('admin') ? '/finance/reconcile' : undefined}
            />
            <Vital
              label="Compliance" tone="amber"
              value={(counts.complianceDueSoon ?? 0) > 0 ? `${counts.complianceDueSoon} due` : 'Current'}
              sub={(counts.complianceDueSoon ?? 0) > 0 ? 'due soon' : 'nothing pending'} to="/compliance"
            />
            <Vital
              label="Approvals" tone="blue"
              value={(counts.pendingApprovals ?? 0) > 0 ? `${counts.pendingApprovals}` : 'Clear'}
              sub={(counts.pendingApprovals ?? 0) > 0 ? 'pending' : 'none waiting'} to="/workflows"
            />
            <Vital
              label="Bills" tone={(counts.overdueBills ?? 0) > 0 ? 'rose' : 'slate'}
              value={(counts.overdueBills ?? 0) > 0 ? `${counts.overdueBills} overdue` : 'None overdue'}
              sub={(counts.overdueBills ?? 0) > 0 ? 'past due' : 'all current'} to="/finance/bills"
            />
          </div>
        </Card>
      )}

      {/* ── First-run guidance (new tenants only — both self-suppress once set up) ── */}
      {!loading && (
        <>
          {isFirstRun && <GetStartedHero deadlines={counts.complianceDueSoon ?? 0} />}
          <FirstRunChecklist />
          <FormationReadinessCard />
        </>
      )}

      {/* ── Needs attention: alerts that otherwise live only in the sidebar
            (W-9s, policies, onboarding, AI budget) — homed here so collapsing the
            sidebar attention block to a pill never strands them. ── */}
      {!loading && homeAttn.length > 0 && <HomeAttentionCards items={homeAttn} />}

      {/* ── Adaptive center: schedule-spine (paid-for-work) OR A/R (billing) ── */}
      {!loading && (operatingModel === 'paid_for_work'
        ? <ExpectedRevenueCard />
        : <ArAgingCenter data={aging} loading={agingLoading} />)}

      {/* ── Do now + Tier-2 gap-closers ── */}
      {!loading && (verdict.gapClosers.length > 0 || nowItem) && (
        <Card padding="md" className="border-l-4 border-l-primary-500">
          {verdict.gapClosers.length > 0 ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 px-2.5 py-1 text-xs font-bold">
                <Landmark className="w-3.5 h-3.5" /> Close the gap
              </span>
              <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-white mt-2.5">
                Ways to cover the shortfall
              </h2>
              <div className="mt-3 space-y-2">
                {verdict.gapClosers.map((g) => (
                  <Link key={g.route + g.label} to={g.route}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5 hover:border-primary-500 transition-colors group">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 dark:text-slate-100"><GlossaryText text={g.label} inlineFirstUse={false} /></p>
                      <p className="text-xs text-slate-600 dark:text-slate-400"><GlossaryText text={g.detail} inlineFirstUse={false} /></p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 mt-0.5" />
                  </Link>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-slate-400 dark:text-slate-500">
                Options to consider — not advice. You decide and act; Nexus never moves money on its own.
              </p>
            </>
          ) : nowItem ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2.5 py-1 text-xs font-bold">
                <span className="w-2 h-2 rounded-full bg-current" /> Do now
                {nowItem.estimated_minutes ? ` · ${nowItem.estimated_minutes} min` : ''}
              </span>
              <PriorityCardRow item={nowItem} prominent />
            </>
          ) : null}
        </Card>
      )}

      {/* ── Money / Compliance lanes ── */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Lane title="Money" items={moneyItems}
            emptyHint={(counts.overdueBills ?? 0) > 0 ? `${counts.overdueBills} bill(s) overdue` : 'Nothing needs collecting or paying.'} />
          <Lane title="Compliance & entity" items={complianceItems}
            emptyHint={(counts.complianceDueSoon ?? 0) > 0 ? `${counts.complianceDueSoon} item(s) due soon` : 'Compliance is current.'} />
        </div>
      )}

      {/* ── Drill links + evidence footer + basis disclaimer ── */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuickLink to="/reports" icon={BarChart3} label="Financials" />
          <QuickLink to="/capture" icon={ScanLine} label="Capture" />
          <QuickLink to="/invoices" icon={FileText} label="Invoices" />
          <QuickLink to="/finance/cashflow" icon={Wallet} label="Cash flow" />
        </div>
        {feed.items.length > 0 && (
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Ranked by {feed.ranking_method} · {feed.total_items_considered} items considered
            {feed.filtered_by_role > 0 && ` · ${feed.filtered_by_role} hidden by role`}
          </p>
        )}
        <div className="flex items-center justify-center gap-2 flex-wrap text-center">
          <BasisBadge basis="cash" />
          <span className="text-[10px] text-slate-400 dark:text-slate-500">
            Company lens — no personal data. Cash figures from the bank-reconciled forecast. Informational decision-support, not tax, legal, or accounting advice.
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- pieces ----

// Home Redesign Step 1 (2026-06-17): the "Needs attention" card strip. Renders
// the homeCard attention rows (already role+count filtered by computeAttentionState)
// so the sidebar-only alerts get a first-class Home surface.
const ATTN_TONE: Record<AttentionItem['variant'], string> = {
  urgent: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};
const ATTN_BORDER: Record<AttentionItem['variant'], string> = {
  urgent: 'border-l-rose-500', warn: 'border-l-amber-500', info: 'border-l-blue-500',
};

function HomeAttentionCards({ items }: { items: AttentionItem[] }) {
  return (
    <Card padding="md">
      <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-white mb-3">Needs attention</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((i) => {
          const Icon = i.icon;
          return (
            <Link key={`${i.label}-${i.to}`} to={i.to}
              className={`group flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 border-l-4 ${ATTN_BORDER[i.variant]} bg-white dark:bg-slate-800 px-3.5 py-3 hover:border-primary-500 transition-colors`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${ATTN_TONE[i.variant]}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 leading-tight"><GlossaryText text={i.label} inlineFirstUse={false} /></p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {i.excludeFromTotal ? 'Manage AI usage →' : `${i.count} to handle →`}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0 mt-1" />
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

const TONE: Record<string, string> = {
  emerald: 'before:bg-emerald-500', amber: 'before:bg-amber-500',
  blue: 'before:bg-blue-500', rose: 'before:bg-rose-500', slate: 'before:bg-slate-400',
};

function Vital({ label, value, sub, tone, to }: {
  label: string; value: string; sub: string; tone: string; to?: string;
}) {
  const inner = (
    <div className={`relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 min-w-0 before:absolute before:left-0 before:top-0 before:h-1 before:w-full ${TONE[tone] ?? TONE.slate} ${to ? 'hover:border-primary-500 transition-colors' : ''}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">{label}</p>
      <p className="font-mono text-lg sm:text-xl font-bold text-slate-900 dark:text-white truncate">{value}</p>
      <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{sub}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function Lane({ title, items, emptyHint }: { title: string; items: CommandCenterItem[]; emptyHint: string }) {
  return (
    <Card padding="md">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-2">{emptyHint}</p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((i) => <PriorityCardRow key={i.id} item={i} />)}
        </div>
      )}
    </Card>
  );
}

function urgencyPill(daysUntil: number): { label: string; cls: string } {
  if (daysUntil < 0) return { label: `${Math.abs(daysUntil)}d overdue`, cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' };
  if (daysUntil === 0) return { label: 'today', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
  if (daysUntil <= 7) return { label: `${daysUntil}d`, cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' };
  return { label: `${daysUntil}d`, cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300' };
}

function PriorityCardRow({ item, prominent }: { item: CommandCenterItem; prominent?: boolean }) {
  const Icon = CARD_TYPE_ICON[item.card_type] ?? Sparkles;
  const toneCls = CARD_TYPE_TONE[item.card_type] ?? CARD_TYPE_TONE.checklist;
  const urgency = urgencyPill(item.days_until);
  const amount = fmtCents(item.amount_cents);
  const dueDate = item.due_date ? formatISODate(item.due_date) : null;
  const ariaLabel = item.subtitle ? `${item.title} — ${item.subtitle}` : item.title;
  return (
    <Link to={item.click_route || '#'} aria-label={ariaLabel}
      className={`block group ${prominent ? 'mt-3' : 'py-2.5 first:pt-0'}`}>
      <div className={prominent ? 'flex items-start gap-3' : 'flex items-start gap-3'}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${toneCls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className={`font-semibold text-slate-900 dark:text-slate-100 leading-tight ${prominent ? 'text-base' : 'text-sm'}`}><GlossaryText text={item.title} inlineFirstUse={false} /></h3>
            {amount && <span className="font-mono font-medium text-slate-900 dark:text-slate-100 text-xs sm:text-sm whitespace-nowrap">{amount}</span>}
          </div>
          {item.subtitle && <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2"><GlossaryText text={item.subtitle} inlineFirstUse={false} /></p>}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[11px]">
            <span className={`px-2 py-0.5 rounded ${urgency.cls}`}>{urgency.label}</span>
            {item.owner_role && <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 capitalize">{item.owner_role}</span>}
            {dueDate && <span className="text-slate-500 dark:text-slate-400">{dueDate}</span>}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0 mt-1" />
      </div>
    </Link>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors text-sm">
      <Icon className="w-4 h-4" /><span>{label}</span>
    </Link>
  );
}
